import React, { useState } from 'react';
import {
  Alert, Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle,
} from '@mui/material';
import apiClient from '../../services/apiClient';

/**
 * 999.18427 — Promote/Demote admin for one user row, behind a confirm dialog.
 * PUT /api/admin/users/:id/role { role }. Never rendered for the acting
 * admin's own row (the backend refuses self role changes too).
 */
function refusalMessage(err) {
  if (err.data && err.data.error === 'STEP_UP_REQUIRED') {
    return 'Changing a role needs a recent two-factor check. Sign out and back in with your authenticator code, then try again.';
  }
  return err.message;
}

export default function RoleChangeControl({ user, currentUserId, onChanged }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  if (user.id === currentUserId) return null;

  const promoting = user.role !== 'admin';
  const nextRole = promoting ? 'admin' : 'user';
  const label = promoting ? 'Make admin' : 'Remove admin';

  function close() {
    setOpen(false);
    setError(null);
  }

  async function confirm() {
    setBusy(true);
    setError(null);
    try {
      await apiClient.put(`/api/admin/users/${user.id}/role`, { role: nextRole });
      setOpen(false);
      onChanged();
    } catch (err) {
      setError(refusalMessage(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <Button
        size="small" variant="outlined" color={promoting ? 'secondary' : 'warning'}
        data-role-control=""
        onClick={() => setOpen(true)}
        sx={{ fontSize: '0.65rem', py: 0.25, px: 1, minWidth: 0, textTransform: 'none' }}
      >
        {label}
      </Button>
      <Dialog open={open} onClose={close}>
        <DialogTitle>{label}?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {promoting
              ? `${user.email} will get full admin access to this console.`
              : `${user.email} will lose admin access.`}
            {' '}The new role applies at their next token refresh (within 15 minutes).
          </DialogContentText>
          {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
        </DialogContent>
        <DialogActions>
          <Button onClick={close} disabled={busy}>Cancel</Button>
          <Button onClick={confirm} disabled={busy} color={promoting ? 'secondary' : 'warning'} variant="contained">
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
